import { createFileRoute, notFound, redirect } from "@tanstack/react-router";

import { ThemePage } from "../components/lidoo/theme-page";
import { clubsByTheme } from "../lib/api/lidoo.functions";
import { DICTS, fill, regionName, LOCALIZED_LANGS, type Lang } from "../lib/i18n";
import { isIndexableLocale, NOINDEX_META } from "../lib/indexing";
import { catalogueHreflang } from "../lib/seo";
import { MIN_CLUBS_PER_THEME_PAGE, themeFromSlug } from "../lib/themes";

export const Route = createFileRoute("/$locale/theme/$theme/$region")({
  beforeLoad: ({ params }) => {
    if (params.locale === "en")
      throw redirect({
        to: "/theme/$theme/$region",
        params: { theme: params.theme, region: params.region },
      });
    if (!LOCALIZED_LANGS.includes(params.locale as Lang)) throw notFound();
  },
  loader: async ({ params }) => {
    const theme = themeFromSlug(params.theme);
    if (!theme) throw notFound();
    const res = await clubsByTheme({ data: { theme, regionKey: params.region } });
    if (res.clubs.length < MIN_CLUBS_PER_THEME_PAGE) throw notFound();
    return { ...res, theme };
  },
  head: (ctx) => {
    const d = DICTS[ctx.params.locale as Lang];
    const theme = ctx.loaderData?.theme;
    if (!d || !theme) return {};
    const what = d[`th_${theme}`] ?? "";
    const place = regionName(
      ctx.params.region,
      ctx.loaderData?.regionLabel ?? "",
      (k) => d[k] ?? k,
    );
    const heading = fill(d.th_in, { what, place });
    return {
      meta: [
        { title: `${heading} | sunbedly` },
        {
          name: "description",
          content: fill(d.th_meta, {
            what,
            place,
            n: String(ctx.loaderData?.clubs.length ?? 0),
          }),
        },
        // Machine-translated languages stay out of the index (lib/indexing.ts).
        ...(isIndexableLocale(ctx.params.locale) ? [] : [NOINDEX_META]),
      ],
      links: catalogueHreflang(`/theme/${ctx.params.theme}/${ctx.params.region}`, ctx.params.locale),
    };
  },
  component: LocaleThemeRegionPage,
});

function LocaleThemeRegionPage() {
  const { theme, clubs, regions, regionLabel } = Route.useLoaderData();
  const { region } = Route.useParams();
  return (
    <ThemePage
      theme={theme}
      clubs={clubs}
      regions={regions}
      regionKey={region}
      regionLabel={regionLabel}
    />
  );
}
